import { Button, ModalBody, ModalFooter, ModalHeader } from "@chakra-ui/react"
import { useRouter } from "next/router"
import { useEffect, useRef, useState } from "react"
import { fileTypes } from "../../common/constants/constants"
import { getArticleDownloadZipUrl, getProductDownloadZipUrl } from "../../common/hooks/queries"
import { useBrandedTranslation } from "../../common/hooks/useBrandedTranslation"
import { useDownloadFile } from "../../common/hooks/useDownloadFile"
import { useAuth } from "../../common/hooks/useGigya"
import { AvailableZipOptionsType } from "../../common/types/product"
import { UseModalReturnTypes } from "../../common/types/useModal"
import {
  getDefaultSelectedOptions,
  getSelectedOptionsAsStrings,
  isCadDrawingOption,
} from "../../common/utils/article-table"
import { checkIsProduct } from "../../common/utils/product-utils"
import DownloadButton from "./DownloadButton/DownloadButton"
import ZipOptionsForm from "./ZipOptionsForm"

type Props = UseModalReturnTypes & {
  title: string
  availableZipOptions: AvailableZipOptionsType
  itemId: string
  handleClose: () => void
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  getDownloadZipUrl?: any
  fileName?: string
}

/* Modal used for download of ZIPs both from article table and on product level, available only for desktop and tablet */
const DownloadZipModal = ({
  Modal,
  handleClose,
  title,
  availableZipOptions,
  itemId,
  getDownloadZipUrl,
  fileName,
}: Props) => {
  const { t } = useBrandedTranslation()
  const { locale, query } = useRouter()
  const brand = String(query.brand)
  const { accountInfo } = useAuth()

  const isFileDownloadStarted = useRef(false)

  const isProduct = checkIsProduct(itemId) // Check if product or article
  if (!getDownloadZipUrl) {
    getDownloadZipUrl = isProduct ? getProductDownloadZipUrl : getArticleDownloadZipUrl
  }
  if (!fileName) {
    fileName = (isProduct ? "ProductDataPackage" : "CADDrawings") + "_" + itemId
  }

  /* If user logged in, get the pre-selected file types for download from the user settings */
  const userPreselectedOptions = accountInfo?.settings?.downloads ?? null

  /* Hold all the rendered checkboxes values in this state */
  const [renderedOptions, setRenderedOptions] = useState<Partial<AvailableZipOptionsType>>(
    getDefaultSelectedOptions(availableZipOptions, userPreselectedOptions)
  )

  /* These are the stringified options passed to the API */
  const selectedOptionsAsString = getSelectedOptionsAsStrings(renderedOptions, locale).toString()

  /* Determine if there are any files selected for download at all */
  const isDownloadEnabled = Object.entries(renderedOptions).some((opt) => opt[1])

  /* Determine if we have one column with checkboxes in the modal or two to adjust size accordingly */
  const renderedOptionsArray = Object.entries(renderedOptions)
  const isDoubleColumns =
    renderedOptionsArray.some((opt) => isCadDrawingOption(opt[0])) &&
    renderedOptionsArray.some((opt) => !isCadDrawingOption(opt[0]))

  /* Initiate hook for zip file download */
  const { onClickDownloadFile: onClickDownloadZip, isFileLoading: isZipFileDownloading } = useDownloadFile(
    itemId,
    brand,
    locale,
    fileTypes.ZIP,
    getDownloadZipUrl,
    selectedOptionsAsString,
    fileName
  )

  useEffect(() => {
    /* If file download has been initiated flag it in local ref */
    if (isZipFileDownloading) isFileDownloadStarted.current = true

    /* If file download has been completed, reset the ref and close the modal */
    if (!isZipFileDownloading && isFileDownloadStarted.current === true) {
      isFileDownloadStarted.current = false
      setTimeout(() => handleClose(), 100)
    }
  }, [isZipFileDownloading, handleClose])

  return (
    <Modal
      modalContentStyleProps={{ mx: { base: "4", sm: "0" } }}
      customOnClose={handleClose}
      size={isDoubleColumns ? "xl" : "md"}
    >
      <ModalHeader mt="8" px={{ base: "4", sm: "8" }} fontWeight="medium" lineHeight="7">
        {title}
      </ModalHeader>

      <ModalBody py="0" px={{ base: "4", sm: "8" }}>
        <ZipOptionsForm renderedOptions={renderedOptions} setRenderedOptions={setRenderedOptions} />
      </ModalBody>

      <ModalFooter
        px={{ base: "4", sm: "8" }}
        py={{ base: "10", sm: "8" }}
        justifyContent={{ base: "center", sm: "flex-end" }}
        gap="6"
      >
        <Button
          variant="secondary"
          border="1px solid"
          color="black"
          onClick={handleClose}
          w={{ base: "100%", sm: "auto" }}
        >
          {t("popup-cancel")}
        </Button>
        <DownloadButton
          className="popup-download-zip" // GTM Event: ZIP file download
          isLoading={isZipFileDownloading}
          isDisabled={!isDownloadEnabled}
          onClick={onClickDownloadZip}
          label={t("popup-download")}
          variant="primary"
          color="white"
        />
      </ModalFooter>
    </Modal>
  )
}

export default DownloadZipModal
